import React from "react";
import { Modal, Box, Typography, IconButton } from "@mui/material";
import { LuX, LuCheckCircle2 } from "react-icons/lu";

const DARK_NAVY = "#051525";
const DARK_NAVY_LIGHT = "#4a6278";
const BLUE = "#1E3A5F";
const BTN_HOVER = "#162C47";
const PROFILE_FONT = '"Inter", sans-serif';

interface ConfirmationModalProps {
  open: boolean;
  type: "phone" | "email" | "bank" | "text" | "address";
  value?: string;
  onConfirm: () => void;
  onClose: () => void;
}

const successConfig: Record<
  string,
  { title: string; message: string }
> = {
  phone: {
    title: "Phone Number Change Requested",
    message:
      "Your phone number change has been submitted for verification. Existing details will remain active until approval.",
  },
  email: {
    title: "Email Change Requested",
    message:
      "Your email change has been submitted for verification. Existing details will remain active until approval.",
  },
  bank: {
    title: "Bank Details Update Requested",
    message:
      "Your request to update bank details has been submitted to our compliance team.",
  },
  default: {
    title: "Update Request Submitted",
    message:
      "Your request has been successfully submitted and is pending verification.",
  },
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  type,
  onConfirm,
  onClose,
}) => {
  const config = successConfig[type] || successConfig.default;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          bgcolor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 16px 48px rgba(0,0,0,0.18)",
          width: 480,
          maxWidth: "100%",
          fontFamily: PROFILE_FONT,
          position: "relative",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 36,
            height: 36,
            border: "1px solid #E0E0E0",
            color: DARK_NAVY,
          }}
        >
          <LuX size={18} />
        </IconButton>

        <Box sx={{ p: { xs: 3, sm: 4 }, pt: 4 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "rgba(8, 153, 31, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <LuCheckCircle2 size={28} color="#08991F" />
          </Box>

          <Typography
            id="modal-title"
            sx={{
              fontSize: 20,
              fontWeight: 700,
              color: DARK_NAVY,
              mb: 1,
            }}
          >
            {config.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: DARK_NAVY_LIGHT,
              lineHeight: 1.6,
              mb: 1,
            }}
          >
            {config.message}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            px: 3,
            py: 2,
            borderTop: "1px solid #F0F0F0",
            bgcolor: "#FAFAFA",
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={onConfirm}
            sx={{
              px: 4,
              py: 1.25,
              fontSize: 14,
              fontWeight: 600,
              fontFamily: PROFILE_FONT,
              color: "#ffffff",
              bgcolor: BLUE,
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              minWidth: 120,
              "&:hover": { bgcolor: BTN_HOVER },
            }}
          >
            OK
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmationModal;
